import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Download, Globe, Laptop, ShieldAlert, ShieldCheck, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  AutoRefreshControl, SLOW_REFRESH_INTERVALS, useRefreshInterval,
} from '@/components/ui/auto-refresh-control'
import { exportElementToPdf } from '@/lib/exportElementToPdf'
import { cn } from '@/lib/utils'
import { loginAuditApi, type LoginAuditRow, type Tally } from '../loginAuditApi'
import { formatDateTime } from '@/lib/dateUtils'

/**
 * Login activity — every authentication attempt made against the estate.
 *
 * The screen this replaces returned a hard-coded array from the browser. What is on this one is
 * what the auth paths actually wrote, which is why the columns are the ones an incident needs:
 * WHO was attempted (even when no such account exists), FROM WHERE, ON WHAT, and WHY it failed.
 *
 * FAILURES ARE THE DEFAULT VIEW. Successes are the bulk of the rows and the least interesting of
 * them; opening on "All" buries the run of attempts that is the reason anyone came here.
 */

const WINDOWS = [
  { value: '1', label: '24h' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
] as const

const OUTCOMES = [
  { value: 'failure', label: 'Failed' },
  { value: 'success', label: 'Succeeded' },
  { value: 'all', label: 'All' },
] as const

/** Human wording for the stored codes. The codes stay short and stable; the labels can move. */
const EVENT_LABEL: Record<string, string> = {
  password_attempt: 'Password',
  mfa_attempt: 'MFA',
  passkey_attempt: 'Passkey',
  logout: 'Sign out',
  lockout: 'Rate limited',
  device_enrolled: 'Device enrolled',
  device_revoked: 'Device revoked',
  enrolment_code_attempt: 'Enrolment code',
}

const REASON_LABEL: Record<string, string> = {
  unknown_user: 'No such account',
  bad_password: 'Wrong password',
  no_password_set: 'No password set',
  account_inactive: 'Account disabled',
  mfa_invalid: 'Wrong MFA code',
  enrolment_code_invalid: 'Wrong enrolment code',
  mfa_required: 'MFA not completed',
  passkey_invalid: 'Passkey rejected',
  rate_limited: 'Too many attempts',
  server_error: 'Server error',
}

function Stat({ label, value, tint, icon }: {
  label: string; value: string; tint: string; icon?: React.ReactNode
}) {
  return (
    <div className={cn('rounded-xl px-4 py-3', tint)}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon}{label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  )
}

/**
 * A ranked tally. Rendered as a bar against the largest value rather than a percentage of the
 * total: the question is which of these is the outlier, not what share of a whole it holds.
 */
function TallyList({ title, rows, empty, onPick }: {
  title: string; rows: Tally[]; empty: string; onPick?: (value: string) => void
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0)
  return (
    <div className="rounded-xl border p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</div>
      {rows.length === 0 ? (
        <div className="py-3 text-xs text-muted-foreground">{empty}</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <button
              key={r.value}
              type="button"
              disabled={!onPick}
              onClick={() => onPick?.(r.value)}
              className={cn(
                'relative flex w-full items-center justify-between overflow-hidden rounded px-2 py-1 text-left text-xs',
                onPick && 'hover:bg-muted/60',
              )}
            >
              <span
                className="absolute inset-y-0 left-0 bg-muted"
                style={{ width: `${max ? (r.count / max) * 100 : 0}%` }}
                aria-hidden
              />
              <span className="relative truncate pr-2">{r.value}</span>
              <span className="relative tabular-nums text-muted-foreground">{r.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function LoginActivityPage() {
  const { t } = useTranslation()
  const sheet = useRef<HTMLDivElement>(null)

  const [days, setDays] = useState<string>('7')
  const [outcome, setOutcome] = useState<'failure' | 'success' | 'all'>('failure')
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState<{ username?: string; ip?: string }>({})
  const [page, setPage] = useState(0)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [exporting, setExporting] = useState(false)

  const [refresh, setRefresh] = useRefreshInterval(
    'vs-login-audit-refresh', 60_000, SLOW_REFRESH_INTERVALS,
  )

  const dayCount = Number(days)
  const LIMIT = 100

  const filters = useMemo(() => ({
    days: dayCount, outcome, limit: LIMIT, offset: page * LIMIT, ...applied,
  }), [dayCount, outcome, page, applied])

  const list = useQuery({
    queryKey: ['login-audit', filters],
    queryFn: () => loginAuditApi.list(filters),
    refetchInterval: refresh,
  })

  const summary = useQuery({
    queryKey: ['login-audit-summary', dayCount],
    queryFn: () => loginAuditApi.summary(dayCount),
    refetchInterval: refresh,
  })

  /** An entry that looks like an address filters on address; anything else filters on username. */
  const applySearch = () => {
    const term = search.trim()
    setPage(0)
    if (!term) return setApplied({})
    const isAddress = /^[0-9a-f:.]+$/i.test(term) && /[.:]/.test(term)
    setApplied(isAddress ? { ip: term } : { username: term.toLowerCase() })
  }

  const clearFilter = (which: 'username' | 'ip') => {
    setApplied((a) => { const next = { ...a }; delete next[which]; return next })
    setSearch('')
    setPage(0)
  }

  const exportPdf = async () => {
    setExporting(true)
    try {
      await exportElementToPdf({
        el: sheet.current,
        filename: `login-activity-${days}d.pdf`,
        title: t('loginAudit.title', 'Login activity'),
        subtitle: t('loginAudit.exportSubtitle', 'Last {{days}} days · {{outcome}}', {
          days, outcome: OUTCOMES.find((o) => o.value === outcome)?.label ?? outcome,
        }),
      })
    } finally {
      setExporting(false)
    }
  }

  const s = summary.data
  const total = (s?.success ?? 0) + (s?.failure ?? 0)
  const failRate = total ? Math.round(((s?.failure ?? 0) / total) * 100) : 0
  const rows = list.data?.rows ?? []
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / LIMIT))

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('loginAudit.title', 'Login activity')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('loginAudit.subtitle',
              'Every attempt to sign in, successful or not, with the account tried, the address it came from and the device used.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AutoRefreshControl
            value={refresh}
            onChange={setRefresh}
            onRefresh={() => { void list.refetch(); void summary.refetch() }}
            isRefreshing={list.isFetching || summary.isFetching}
            updatedAt={list.dataUpdatedAt}
            intervals={SLOW_REFRESH_INTERVALS}
          />
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                  onClick={() => void exportPdf()} disabled={exporting}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {exporting ? t('common.exporting', 'Exporting…') : t('common.exportPdf', 'Export PDF')}
          </Button>
        </div>
      </div>

      <div ref={sheet} className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl
            size="sm"
            ariaLabel={t('loginAudit.windowLabel', 'Time window')}
            value={days}
            onValueChange={(v) => { setDays(v); setPage(0) }}
            options={WINDOWS.map((w) => ({ value: w.value, label: t(`loginAudit.window${w.value}`, w.label) }))}
          />
          <SegmentedControl
            size="sm"
            ariaLabel={t('loginAudit.outcomeLabel', 'Outcome')}
            value={outcome}
            onValueChange={(v) => { setOutcome(v as typeof outcome); setPage(0) }}
            options={OUTCOMES.map((o) => ({ value: o.value, label: t(`loginAudit.outcome_${o.value}`, o.label) }))}
          />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch() }}
              onBlur={applySearch}
              placeholder={t('loginAudit.searchPlaceholder', 'Account or IP address')}
              className="h-7 w-56 pl-7 text-xs"
            />
          </div>
          {applied.username && (
            <button type="button" onClick={() => clearFilter('username')}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
              {applied.username}<X className="h-3 w-3" />
            </button>
          )}
          {applied.ip && (
            <button type="button" onClick={() => clearFilter('ip')}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
              {applied.ip}<X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('loginAudit.statSuccess', 'Successful')} value={String(s?.success ?? 0)}
                tint="bg-emerald-50 dark:bg-emerald-950/40"
                icon={<ShieldCheck className="h-3.5 w-3.5" />} />
          <Stat label={t('loginAudit.statFailure', 'Failed')} value={String(s?.failure ?? 0)}
                tint="bg-rose-50 dark:bg-rose-950/40"
                icon={<ShieldAlert className="h-3.5 w-3.5" />} />
          <Stat label={t('loginAudit.statFailRate', 'Failure rate')} value={`${failRate}%`}
                tint="bg-amber-50 dark:bg-amber-950/40" />
          <Stat label={t('loginAudit.statCountries', 'Countries seen')}
                value={String(s?.countries.length ?? 0)}
                tint="bg-sky-50 dark:bg-sky-950/40"
                icon={<Globe className="h-3.5 w-3.5" />} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TallyList title={t('loginAudit.topAccounts', 'Most failed accounts')}
                     rows={s?.topFailedUsernames ?? []}
                     empty={t('loginAudit.noFailures', 'No failed attempts in this window.')}
                     onPick={(v) => { setSearch(v); setApplied({ username: v }); setOutcome('failure'); setPage(0) }} />
          <TallyList title={t('loginAudit.topIps', 'Most failed addresses')}
                     rows={s?.topFailedIps ?? []}
                     empty={t('loginAudit.noFailures', 'No failed attempts in this window.')}
                     onPick={(v) => { setSearch(v); setApplied({ ip: v }); setOutcome('failure'); setPage(0) }} />
          <TallyList title={t('loginAudit.reasons', 'Why they failed')}
                     rows={(s?.failureReasons ?? []).map((r) => ({ ...r, value: REASON_LABEL[r.value] ?? r.value }))}
                     empty={t('loginAudit.noFailures', 'No failed attempts in this window.')} />
          <TallyList title={t('loginAudit.browsers', 'Browsers')}
                     rows={s?.browsers ?? []}
                     empty={t('loginAudit.noData', 'Nothing recorded yet.')} />
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">{t('loginAudit.colWhen', 'When')}</th>
                <th className="px-3 py-2 font-medium">{t('loginAudit.colAccount', 'Account tried')}</th>
                <th className="px-3 py-2 font-medium">{t('loginAudit.colStep', 'Step')}</th>
                <th className="px-3 py-2 font-medium">{t('loginAudit.colResult', 'Result')}</th>
                <th className="px-3 py-2 font-medium">{t('loginAudit.colFrom', 'From')}</th>
                <th className="px-3 py-2 font-medium">{t('loginAudit.colDevice', 'Device')}</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </td></tr>
              )}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  {t('loginAudit.empty', 'No attempts match these filters.')}
                </td></tr>
              )}
              {rows.map((r: LoginAuditRow) => (
                <tr key={r.id}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-xs">
                    {formatDateTime(r.occurredAt)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{r.username ?? '—'}</span>
                    {r.userId == null && r.username && (
                      <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                        {t('loginAudit.noAccount', 'no account')}
                      </span>
                    )}
                    {expanded === r.id && r.userAgent && (
                      <div className="mt-1 max-w-[420px] break-all text-[11px] text-muted-foreground">{r.userAgent}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {EVENT_LABEL[r.event] ?? r.event}
                    {r.authMethod && r.authMethod !== 'password' && (
                      <span className="ml-1 text-muted-foreground">({r.authMethod})</span>
                    )}
                    {/* Which enrolled device: the one that signed in, or the one enrolled or revoked. */}
                    {r.deviceLabel && (
                      <div className="text-[11px] text-muted-foreground" data-testid="login-device">{r.deviceLabel}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={cn(
                      'rounded px-1.5 py-0.5 text-[11px]',
                      r.outcome === 'success'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
                    )}>
                      {r.outcome === 'success'
                        ? t('loginAudit.ok', 'OK')
                        : (REASON_LABEL[r.failureReason ?? ''] ?? r.failureReason ?? t('loginAudit.failed', 'Failed'))}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="tabular-nums">{r.ip ?? '—'}</div>
                    {(r.city || r.country) && (
                      <div className="text-muted-foreground">
                        {[r.city, r.region, r.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {expanded === r.id && r.forwardedFor && (
                      <div className="mt-1 max-w-[280px] break-all text-[11px] text-muted-foreground">
                        {t('loginAudit.chain', 'Chain')}: {r.forwardedFor}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Laptop className="h-3.5 w-3.5 text-muted-foreground" />
                      {[r.browser, r.browserVersion].filter(Boolean).join(' ') || '—'}
                    </div>
                    <div className="text-muted-foreground">
                      {[r.os, r.deviceType].filter(Boolean).join(' · ') || ''}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t('loginAudit.pageOf', 'Page {{page}} of {{pages}} · {{total}} attempts',
                { page: page + 1, pages, total: list.data?.total ?? 0 })}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                      disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                {t('common.previous', 'Previous')}
              </Button>
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                      disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                {t('common.next', 'Next')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
